import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { InstagramLogo, YoutubeLogo, TiktokLogo, Heart } from "@phosphor-icons/react";

function AnimatedCounter({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return (
    <span ref={ref} className="font-['Barlow_Condensed'] tabular-nums">
      {count.toLocaleString()}{suffix}
    </span>
  );
}

const stats = [
  {
    icon: InstagramLogo,
    platform: "Instagram",
    value: 1200000,
    display: "1.2M",
    suffix: "+",
    label: "Followers",
    color: "#E4405F",
    span: "col-span-1 md:col-span-2",
  },
  {
    icon: YoutubeLogo,
    platform: "YouTube",
    value: 450000,
    display: "450K",
    suffix: "+",
    label: "Subscribers",
    color: "#FF0000",
    span: "col-span-1 md:col-span-2",
  },
  {
    icon: TiktokLogo,
    platform: "TikTok",
    value: 800000,
    display: "800K",
    suffix: "+",
    label: "Followers",
    color: "#fff",
    span: "col-span-1 md:col-span-2",
  },
  {
    icon: Heart,
    platform: "Engagement",
    value: 4.8,
    display: "4.8",
    suffix: "%",
    label: "Avg. Engagement Rate",
    color: "#FF3B30",
    span: "col-span-1 md:col-span-3",
    isDecimal: true,
  },
  {
    icon: null,
    platform: "Avg. Reach",
    value: 500000,
    display: "500K",
    suffix: "+",
    label: "Per Post",
    color: "#007AFF",
    span: "col-span-1 md:col-span-3",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function SocialStats() {
  return (
    <section
      id="social-stats"
      data-testid="social-stats-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A] relative"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
            The Numbers
          </p>
          <h2
            data-testid="stats-heading"
            className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white"
          >
            Social Proof That Speaks
          </h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6"
        >
          {stats.map((stat, i) => (
            <motion.div
              key={stat.platform}
              variants={itemVariants}
              data-testid={`stat-card-${stat.platform.toLowerCase().replace(/\s/g, '-')}`}
              className={`${stat.span} bg-[#121212] border border-white/10 rounded-xl p-8 hover:-translate-y-1 hover:border-white/30 transition-all duration-300 group`}
            >
              <div className="flex items-center gap-3 mb-4">
                {stat.icon && (
                  <stat.icon
                    size={28}
                    weight="fill"
                    style={{ color: stat.color }}
                    className="opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                )}
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/50">
                  {stat.platform}
                </span>
              </div>
              <div className="text-5xl sm:text-6xl font-black text-white leading-none mb-2">
                {stat.isDecimal ? (
                  <span className="font-['Barlow_Condensed']">{stat.display}{stat.suffix}</span>
                ) : (
                  <>
                    <AnimatedCounter target={stat.value} suffix="" />
                    <span className="text-[#FF3B30]">{stat.suffix}</span>
                  </>
                )}
              </div>
              <p className="text-sm text-white/40 uppercase tracking-wider font-medium">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
